import React, { useRef } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import {
  IconArrowBack,
  IconArrowForward,
  IconButton,
} from '@noths/polaris-client-ribbons-design-system';
import { toSentenceCase } from '@noths/polaris-client-utils';

import { useCarousel } from './hooks/useCarousel';
import type { LinkCarouselItem, LinkCarouselItemClickPayload, NavigatePayload } from './types';
import * as styles from './LinkCarousel.styles';

export interface LinkCarouselProps {
  items: LinkCarouselItem[];
  onItemClick?: (
    evt: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    payload: LinkCarouselItemClickPayload,
  ) => void;
  onNavigate?: (payload: NavigatePayload) => void;
  onVisible?: () => void;
  title?: string;
}

export const LinkCarousel = ({
  items,
  onItemClick,
  onNavigate,
  onVisible,
  title,
}: LinkCarouselProps) => {
  const carouselWrapperRef = useImpressionRef<HTMLDivElement>(() => onVisible?.());
  const carouselItems = useRef<(HTMLElement | null)[]>([]);
  const {
    hasNavigation: showNavigation,
    hasNext,
    hasPrevious,
    onFocus,
    onNext,
    onPrevious,
    position,
  } = useCarousel(carouselWrapperRef, carouselItems, onNavigate);

  const createClickHandler =
    <T extends HTMLAnchorElement | HTMLButtonElement>(index: number) =>
    (evt: React.MouseEvent<T>) => {
      onItemClick?.(evt, { index });
    };

  return (
    <section {...(title && { 'aria-label': title })} css={styles.linkCarousel}>
      {title && <h3 css={styles.outsideCarouselHeading}>{toSentenceCase(title)}</h3>}
      <div css={styles.carouselContentWrapper} ref={carouselWrapperRef}>
        <div css={styles.carouselContent} style={{ transform: `translateX(-${position}px)` }}>
          <ul css={styles.links}>
            {items.map((item, index) => (
              <li
                css={styles.itemLink}
                key={`${item.href}-${item.text}`}
                ref={(el) => {
                  carouselItems.current[index] = el;
                }}
              >
                <a
                  css={styles.link}
                  data-active={Boolean(item.active)}
                  href={item.href}
                  onClick={createClickHandler<HTMLAnchorElement>(index)}
                  onFocus={(evt) => onFocus(evt.target)}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {showNavigation && (
        <div css={styles.carouselControls} data-testid="carousel-controls">
          <IconButton
            Icon={IconArrowBack}
            aria-label="Scroll to previous item"
            disabled={!hasPrevious}
            onClick={onPrevious}
            size="small"
          />
          <IconButton
            Icon={IconArrowForward}
            aria-label="Scroll to next item"
            disabled={!hasNext}
            onClick={onNext}
            size="small"
          />
        </div>
      )}
    </section>
  );
};
